import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Calendar, User, Eye, Edit, Clock, AlertCircle, FileText } from 'lucide-react';
import EditJobOfferModal from './EditJobOfferModal';
import BrouillonActionModal from './BrouillonActionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const ManagerHSOffresEnAttente = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pendingOffers, setPendingOffers] = useState([]);
  const [modificationOffers, setModificationOffers] = useState([]);
  const [brouillonOffers, setBrouillonOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBrouillonModal, setShowBrouillonModal] = useState(false);
  const [brouillonAction, setBrouillonAction] = useState(null);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const offers = await response.json();
        // Filter only Horizon Solution offers for current manager
        const hsOffers = offers.filter(offer => 
          offer.company === 'Horizon Solution' && offer.created_by === user.id
        );
        
        const pending = hsOffers.filter(offer => offer.status === 'en_attente');
        const modification = hsOffers.filter(offer => offer.status === 'a_modifier');
        const brouillon = hsOffers.filter(offer => offer.status === 'brouillon');
        
        setPendingOffers(pending);
        setModificationOffers(modification);
        setBrouillonOffers(brouillon);
      } else {
        toast.error('Erreur lors du chargement des offres');
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToManager = () => {
    navigate('/manager-horizon');
  };

  const handlePendingOfferClick = (offer) => {
    // Offres en attente sont en lecture seule
    toast.info('Cette offre est en cours de validation par la RH');
  };

  const handleModificationOfferClick = (offer) => {
    setSelectedOffer(offer);
    setShowEditModal(true);
  };

  const handleOfferUpdated = () => {
    setShowEditModal(false);
    setSelectedOffer(null);
    fetchOffers(); // Refresh offers
  };

  const handleBrouillonOfferClick = (offer) => {
    setSelectedOffer(offer);
    setShowBrouillonModal(true);
  };

  const handleBrouillonAction = async (action) => {
    if (!selectedOffer) return;

    const token = localStorage.getItem('token');
    
    try {
      if (action === 'envoyer') {
        // Envoyer le brouillon à la RH (changer statut vers en_attente)
        const response = await fetch(`${API}/job-offers/${selectedOffer.id}/status?status=en_attente`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          toast.success('Offre envoyée à la RH pour validation');
          setShowBrouillonModal(false);
          setSelectedOffer(null);
          fetchOffers();
        } else {
          toast.error('Erreur lors de l\'envoi de l\'offre');
        }
      } else if (action === 'supprimer') {
        // Supprimer le brouillon
        const response = await fetch(`${API}/job-offers/${selectedOffer.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          toast.success('Brouillon supprimé avec succès');
          setShowBrouillonModal(false);
          setSelectedOffer(null);
          fetchOffers();
        } else {
          toast.error('Erreur lors de la suppression du brouillon');
        }
      } else if (action === 'updated') {
        // Refresh data after update
        fetchOffers();
      }
    } catch (error) {
      console.error('Error handling brouillon action:', error);
      toast.error('Erreur de connexion');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const companyLogos = {
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png'
  };

  return (
    <div className="manager-offers-pending-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToManager}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos['Horizon Solution']}
            alt="Logo Horizon Solution"
            className="rh-header-logo"
          />
        </div>
        <h1>Offres en attente - Horizon Solution</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Tabs defaultValue="en_attente" className="recruitment-tabs">
            <TabsList className="recruitment-tabs-list">
              <TabsTrigger value="en_attente" className="recruitment-tab-trigger">
                En attente
                <span className="recruitment-tab-counter">{pendingOffers.length}</span>
              </TabsTrigger>
              <TabsTrigger value="modification" className="recruitment-tab-trigger">
                Modification
                <span className="recruitment-tab-counter">{modificationOffers.length}</span>
              </TabsTrigger>
              <TabsTrigger value="brouillon" className="recruitment-tab-trigger">
                Brouillon
                <span className="recruitment-tab-counter">{brouillonOffers.length}</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab Content - En attente */}
            <TabsContent value="en_attente" className="recruitment-tab-content">
              <div className="recruitment-grid-compact">
                {pendingOffers.map((offer) => (
                  <Card 
                    key={offer.id}
                    className="recruitment-card-compact cursor-pointer group"
                    onClick={() => handlePendingOfferClick(offer)}
                  >
                    <CardContent className="recruitment-card-compact-content">
                      <div className="recruitment-card-compact-header">
                        <img
                          src={companyLogos['Horizon Solution']}
                          alt="Logo Horizon Solution"
                          className="recruitment-card-compact-logo"
                        />
                        <div className="flex items-center">
                          <Clock className="text-amber-500 mr-2" size={16} />
                          <Eye className="recruitment-card-compact-icon" />
                        </div>
                      </div>
                      <h3 className="recruitment-card-compact-title">
                        {offer.title}
                      </h3>
                      <div className="recruitment-card-compact-info">
                        <div className="recruitment-card-compact-info-item">
                          <User size={14} />
                          <span><strong>Créée par :</strong> {offer.creator_name || 'Vous'}</span>
                        </div>
                        <div className="recruitment-card-compact-info-item">
                          <Calendar size={14} />
                          <span><strong>Date idéale :</strong> {formatDate(offer.date_arrivee)}</span>
                        </div>
                        <div className="recruitment-card-compact-info-item">
                          <Calendar size={14} />
                          <span><strong>Créée le :</strong> {formatDate(offer.created_at)}</span>
                        </div>
                        {offer.type_poste && (
                          <div className="recruitment-card-compact-info-item">
                            <span><strong>Type :</strong> {offer.type_poste}</span>
                          </div>
                        )}
                        {offer.taux_horaire && (
                          <div className="recruitment-card-compact-info-item">
                            <span><strong>Taux :</strong> {offer.taux_horaire}€/h</span>
                          </div>
                        )}
                      </div>
                      <div className="pending-notice">
                        <p className="text-amber-600 text-sm font-medium">En attente de validation RH</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {pendingOffers.length === 0 && (
                  <div className="empty-state-compact">
                    <p>Aucune offre en attente</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab Content - Modification */}
            <TabsContent value="modification" className="recruitment-tab-content">
              <div className="recruitment-grid-compact">
                {modificationOffers.map((offer) => (
                  <Card 
                    key={offer.id}
                    className="recruitment-card-compact recruitment-card-compact-modify cursor-pointer group"
                    onClick={() => handleModificationOfferClick(offer)}
                  >
                    <CardContent className="recruitment-card-compact-content">
                      <div className="recruitment-card-compact-header">
                        <img
                          src={companyLogos['Horizon Solution']}
                          alt="Logo Horizon Solution"
                          className="recruitment-card-compact-logo"
                        />
                        <div className="flex items-center">
                          <AlertCircle className="text-red-500 mr-2" size={16} />
                          <Edit className="recruitment-card-compact-icon" />
                        </div>
                      </div>
                      <h3 className="recruitment-card-compact-title">
                        {offer.title}
                      </h3>
                      <div className="recruitment-card-compact-info">
                        <div className="recruitment-card-compact-info-item">
                          <User size={14} />
                          <span><strong>Créée par :</strong> {offer.creator_name || 'Vous'}</span>
                        </div>
                        <div className="recruitment-card-compact-info-item">
                          <Calendar size={14} />
                          <span><strong>Date idéale :</strong> {formatDate(offer.date_arrivee)}</span>
                        </div>
                        <div className="recruitment-card-compact-info-item">
                          <Calendar size={14} />
                          <span><strong>Modifiée le :</strong> {formatDate(offer.updated_at)}</span>
                        </div>
                        {/* Affichage du message complet de justification de la RH */}
                        {offer.justification_modification && offer.justification_modification.trim() && (
                          <div className="modification-message">
                            <p className="text-red-700 text-sm">
                              <strong>Modification :</strong> {offer.justification_modification}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="modification-notice">
                        <p className="text-red-600 text-sm font-medium">Renvoyée pour modification</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {modificationOffers.length === 0 && (
                  <div className="empty-state-compact">
                    <p>Aucune offre en modification</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab Content - Brouillon */}
            <TabsContent value="brouillon" className="recruitment-tab-content">
              <div className="recruitment-grid-compact">
                {brouillonOffers.map((offer) => (
                  <Card 
                    key={offer.id}
                    className="recruitment-card-compact recruitment-card-compact-draft cursor-pointer group"
                    onClick={() => handleBrouillonOfferClick(offer)}
                  >
                    <CardContent className="recruitment-card-compact-content">
                      <div className="recruitment-card-compact-header">
                        <img
                          src={companyLogos['Horizon Solution']}
                          alt="Logo Horizon Solution"
                          className="recruitment-card-compact-logo"
                        />
                        <div className="flex items-center">
                          <FileText className="text-gray-500 mr-2" size={16} />
                          <Edit className="recruitment-card-compact-icon" />
                        </div>
                      </div>
                      <h3 className="recruitment-card-compact-title">
                        {offer.title}
                      </h3>
                      <div className="recruitment-card-compact-info">
                        <div className="recruitment-card-compact-info-item">
                          <User size={14} />
                          <span><strong>Créée par :</strong> {offer.creator_name || 'Vous'}</span>
                        </div>
                        <div className="recruitment-card-compact-info-item">
                          <Calendar size={14} />
                          <span><strong>Date idéale :</strong> {formatDate(offer.date_arrivee)}</span>
                        </div>
                        <div className="recruitment-card-compact-info-item">
                          <Calendar size={14} />
                          <span><strong>Créée le :</strong> {formatDate(offer.created_at)}</span>
                        </div>
                        {offer.type_poste && (
                          <div className="recruitment-card-compact-info-item">
                            <span><strong>Type :</strong> {offer.type_poste}</span>
                          </div>
                        )}
                        {offer.taux_horaire && (
                          <div className="recruitment-card-compact-info-item">
                            <span><strong>Taux :</strong> {offer.taux_horaire}€/h</span>
                          </div>
                        )}
                      </div>
                      <div className="draft-notice">
                        <p className="text-gray-600 text-sm font-medium">Brouillon enregistré</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {brouillonOffers.length === 0 && (
                  <div className="empty-state-compact">
                    <p>Aucun brouillon</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* Edit Job Offer Modal for modification offers */}
      {showEditModal && selectedOffer && (
        <EditJobOfferModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleOfferUpdated}
          offer={selectedOffer}
        />
      )}

      {/* Brouillon Action Modal */}
      {showBrouillonModal && selectedOffer && (
        <BrouillonActionModal
          isOpen={showBrouillonModal}
          onClose={() => setShowBrouillonModal(false)}
          onAction={handleBrouillonAction}
          offer={selectedOffer}
          userType="horizon_solution"
        />
      )}
    </div>
  );
};

export default ManagerHSOffresEnAttente;